import OliverText from "@builder/components/general/OliverText";
import { addNodeUIReactions } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { BuilderUI, getAllUI, getUIComponent } from "@builder/store/slices/BuilderSlice/builderMap";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { BuildComponentKey } from "@common/build/types";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { addNode, setHovered } from "../../../store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { handleUIdragEnd, handleUIdragStart } from "../OliverBuildFrame/DnD/functions";
import OliverImage from "../OliverImage";
import SidebarCollapse from "../SidebarCollapse";
import SidebarHeader from "../SidebarHeader";
import "./style.scss";
import { useGetExtensionsQuery } from "@builder/store/slices/extensionsApiSlice/extensionsApiSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetCompanyExtensionsQuery } from "@builder/store/slices/extensionsApiSlice/companyExtensionApiSlice";
import { isExtensionInstalled } from "@builder/store/slices/extensionsApiSlice/extension.util";
import { InstalledBuilderElement, useGetBuilderElementsForInstalledExtensionsQuery } from "@builder/store/slices/builderElementsInstalledApiSlice";
import { extendUIWithCustomIframes, getElementToAdd } from "./helper";

function AddElementButton({
    component,
    onClick
}: {
    component: BuilderUI;
    onClick: (key: BuildComponentKey, dynamicElement?: InstalledBuilderElement) => void;
}) {
    const { t } = useTranslation();
    const { key, icon, dynamicElement } = component;

    return (
        <div
            data-key={key}
            onClick={() => onClick(key, dynamicElement)}
            onDragStart={e => handleUIdragStart(e, key, dynamicElement)}
            onDragEnd={e => {
                handleUIdragEnd(e, key, dynamicElement);
            }}
            draggable="true"
        >
            <button>
                <div className="left-wrapper">
                    <OliverImage src={icon} />
                    <OliverText text={dynamicElement?.name || t("builderElements." + component.key)} size="xs" weight="medium-heavy" />
                </div>
            </button>
        </div>
    );
}

const AddElement: React.FC = () => {
    const dispatch = useAppDispatch();
    const selectedNode = useAppSelector(getSelectedNode);
    const gBtype = useAppSelector(state => state.builder.build.globalBlockType);
    const buildType = useAppSelector(state => state.builder.build.buildType);
    const baseLayerType = useAppSelector(state => state.builder.currentLayer.type);
    const buildCompany = useAppSelector(state => state.storeData.company); //Change to build company?
    const selectedBuildCompany = useAppSelector(state => state.builder.build.companyId);
    const currentUIComponent = selectedNode && selectedNode.type !== "global-block" ? getUIComponent(selectedNode.name as BuildComponentKey) : null;
    const uiComponents = getAllUI(selectedNode?.name || "", gBtype, buildType, buildCompany, baseLayerType);

    const { data: extensions } = useGetExtensionsQuery(buildCompany?._id ? buildCompany._id : skipToken);
    const { data: companyExtensions } = useGetCompanyExtensionsQuery(buildCompany?._id ? buildCompany._id : skipToken);
    const { data: builderElementsForInstalledExtensions } = useGetBuilderElementsForInstalledExtensionsQuery(
        selectedBuildCompany? { companyId: selectedBuildCompany } : skipToken
    );

    const filteredUIComponents = useMemo(() => {
        const extensionsList = extensions || [];
        const companyExtensionsList = companyExtensions || [];

        // 1) Start from the base UI components coming from builderMap / getAllUI
        // 2) Extend them with iframe-based custom elements from the API

        const uiWithCustomIframes = extendUIWithCustomIframes(uiComponents, builderElementsForInstalledExtensions);

        // 3) Apply the existing extension-installed filtering logic
        return Object.entries(uiWithCustomIframes).reduce(
            (
                acc: {
                    [key: string]: BuilderUI[];
                },
                [category, elements]
            ) => {
                const filteredElements = elements.filter(element => {
                    if (element.extension) {
                        return isExtensionInstalled(extensionsList, companyExtensionsList, element.extension);
                    }
                    return element;
                });
                if (filteredElements.length > 0) {
                    acc[category] = filteredElements;
                }

                return acc;
            },
            {}
        );
    }, [extensions, companyExtensions, uiComponents, builderElementsForInstalledExtensions]);

    const spreadUIComponents = Object.values(filteredUIComponents).reduce((acc: BuilderUI[], category) => {
        category.forEach(cat => acc.push(cat));
        return acc;
    }, []);
    const [searchInput, setSearchInput] = useState<string>("");
    const { t } = useTranslation();
    const handleSearchInput = (e: React.FormEvent<HTMLInputElement>) => {
        setSearchInput(e.currentTarget.value);
    };

    const clickAddElement = (key: BuildComponentKey, dynamicElement?: InstalledBuilderElement) => {
        const elemToAdd = getElementToAdd(key, dynamicElement);
        dispatch(addNode({ node: elemToAdd }));
        addNodeUIReactions(elemToAdd);
    };

    return (
        <div
            className="add-element"
            onDragOverCapture={() => {
                dispatch(setHovered({ id: null, childIndex: null, direction: null })); // discard the adding of an element if it's dragged back to the sidebar
            }}
        >
            <SidebarHeader title="Add Element" sidebar="left" />

            <div className="find-template-wrapper">
                <img src="/find-template.svg" alt="" />
                {/* <Button
                    buttonStyle="primary"
                    text="Find Template"
                    size="small"
                    className="find-template-button"
                    onClick={() => dispatch(setCurrentPopup("templateLibrary"))}
                /> */}
            </div>
            <div className="add-element-search">
                <div className="search-wrapper">
                    <img className="search" src="/search-icon.svg" alt="" />
                    {searchInput && (
                        <button onClick={() => setSearchInput("")}>
                            <img className="cancel" src="/x-icon-dark.svg" alt="" />
                        </button>
                    )}
                    <input type="text" value={searchInput} placeholder={t("addElement.search")} onInput={handleSearchInput} />
                </div>
            </div>
            <div className="elements-wrapper" style={searchInput ? { padding: "12px", gap: "12px" } : {}}>
                {searchInput &&
                    spreadUIComponents.map((comp, index) => {
                        const search = searchInput.replace(" ", "").toLowerCase();
                        if (comp.label.replace(" ", "").toLowerCase().includes(search)) {
                            return <AddElementButton key={index} component={comp} onClick={clickAddElement} />;
                        }
                    })}
                {!searchInput &&
                    Object.keys(filteredUIComponents).map((key, index) => (
                        <SidebarCollapse key={index} title={t("sidebarCollapse." + key)} open={index === 0}>
                            {filteredUIComponents[key].map((component, index) =>
                                !currentUIComponent || !currentUIComponent.excludedChildren.includes(component.key) ? (
                                    <AddElementButton key={index} component={component} onClick={clickAddElement} />
                                ) : null
                            )}
                        </SidebarCollapse>
                    ))}
            </div>
        </div>
    );
};

export default AddElement;
